/**
 * QuickEmployeeSearch - Autocomplete employee search for ticket creation
 * Adapted from Thaklis app to use ThaklisApiService
 */
import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, Autocomplete, Avatar, IconButton, CircularProgress } from '@mui/material';
import { MdPerson, MdClose, MdPersonSearch } from 'react-icons/md';
import ThaklisApiService from '../../../services/thaklis-api.service';
import AuthenticatedImage from './AuthenticatedImage';
import debounce from 'lodash/debounce';

const QuickEmployeeSearch = ({ onEmployeeSelect, selectedEmployee, required = false, organizationId, initialSearchTerm = '' }) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(selectedEmployee || null);

  const debouncedSearch = useRef(
    debounce(async (term) => {
      if (!term || term.length < 2) {
        setEmployees([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let queryParams = `query=${encodeURIComponent(term)}&includePhoto=true`;
        if (organizationId) queryParams += `&organizationId=${encodeURIComponent(organizationId)}`;

        const response = await ThaklisApiService.get(`v1/employees/search-for-ticket?${queryParams}`);
        // ThaklisApiService returns full axios response, need response.data
        const rawData = response.data;

        let employeeData = [];
        if (rawData && rawData.success) {
          if (Array.isArray(rawData.data)) {
            employeeData = rawData.data;
          } else if (rawData.data?.data && Array.isArray(rawData.data.data)) {
            employeeData = rawData.data.data;
          }
        } else if (Array.isArray(rawData)) {
          employeeData = rawData;
        }

        // Clean up photo URLs
        employeeData = employeeData.map(emp => {
          if (emp.photo && typeof emp.photo === 'string' && emp.photo.startsWith('data:image/jpeg;base64,/v1/users/')) {
            emp.photo = emp.photo.replace('data:image/jpeg;base64,', '');
          }
          return emp;
        });

        setEmployees(employeeData);
        if (employeeData.length > 0) setOpen(true);
        setError('');
      } catch (err) {
        console.error('Error searching employees:', err);
        setError('Failed to search employees');
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    }, 500)
  ).current;

  useEffect(() => {
    return () => { debouncedSearch.cancel(); };
  }, [debouncedSearch]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    if (selectedEmployee) setSelected(selectedEmployee);
  }, [selectedEmployee]);

  const handleEmployeeSelect = (event, employee) => {
    if (!employee) return;

    const processedEmployee = { ...employee };

    // Process photo
    if (processedEmployee.photo && typeof processedEmployee.photo === 'string') {
      if (!processedEmployee.photo.startsWith('data:') && !processedEmployee.photo.startsWith('blob:')) {
        processedEmployee.photo = processedEmployee.photo.startsWith('/')
          ? processedEmployee.photo
          : `data:image/jpeg;base64,${processedEmployee.photo}`;
      }
    }

    // Process documents
    if (processedEmployee.documents && Array.isArray(processedEmployee.documents)) {
      processedEmployee.documents = processedEmployee.documents.map(doc => ({
        ...doc,
        documentType: doc.documentType || doc.type || 'unknown',
        fileName: doc.fileName || doc.name || 'unnamed',
        createdAt: doc.createdAt || doc.uploadDate || new Date().toISOString()
      }));
    }

    setSelected(processedEmployee);
    if (onEmployeeSelect) onEmployeeSelect(processedEmployee);
  };

  const handleClearSelection = () => {
    setSelected(null);
    setSearchTerm('');
    if (onEmployeeSelect) onEmployeeSelect(null);
  };

  // Render selected employee display
  if (selected) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', p: 2.5,
        border: '1px solid', borderColor: 'info.light', borderRadius: '10px',
        bgcolor: 'action.hover', boxShadow: '0 2px 8px rgba(0,131,143,0.08)'
      }}>
        <Box sx={{ position: 'relative', width: 52, height: 52, mr: 2.5 }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: '#00838f', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
            {selected.photo ? (
              <AuthenticatedImage
                src={selected.photo}
                alt={selected.nameEn || 'Employee'}
                employeeId={selected.uid || selected.employeeId}
                maxSize={80}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              <MdPerson size={28} />
            )}
          </Avatar>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
            {selected.nameEn || `${selected.firstNameEn || ''} ${selected.lastNameEn || ''}`.trim() || 'Unknown'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            {selected.uid && (
              <Typography variant="caption" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                UID: {selected.uid}
              </Typography>
            )}
            {selected.mobileNumber && (
              <Typography variant="caption" sx={{ color: '#666' }}>
                {selected.mobileNumber}
              </Typography>
            )}
            {selected.passportNumber && (
              <Typography variant="caption" sx={{ color: '#666' }}>
                Passport: {selected.passportNumber}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton onClick={handleClearSelection} size="small" sx={{
          ml: 1, color: '#999',
          '&:hover': { color: '#e53935', backgroundColor: 'rgba(229,57,53,0.08)' }
        }}>
          <MdClose size={20} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <Autocomplete
        open={open}
        onOpen={() => { if (employees.length > 0) setOpen(true); }}
        onClose={() => setOpen(false)}
        options={employees}
        loading={loading}
        getOptionLabel={(option) => {
          if (!option) return '';
          return option.nameEn || `${option.firstNameEn || ''} ${option.lastNameEn || ''}`.trim() || option.uid || '';
        }}
        isOptionEqualToValue={(option, value) => option?.uid === value?.uid}
        onChange={handleEmployeeSelect}
        inputValue={searchTerm}
        onInputChange={(e, val, reason) => {
          // Only mirror actual typing / clearing into searchTerm. 'reset' fires when an
          // option is picked (we switch to the selected view) — ignore it.
          if (reason === 'input' || reason === 'clear') setSearchTerm(val);
        }}
        filterOptions={(x) => x}
        noOptionsText={searchTerm.length < 2 ? 'Type at least 2 characters to search' : 'No employees found'}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.uid || option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#00838f', fontSize: 14 }}>
              {option.photo ? (
                <AuthenticatedImage
                  src={option.photo}
                  employeeId={option.uid}
                  maxSize={50}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : (
                <MdPerson size={20} />
              )}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                {option.nameEn || `${option.firstNameEn || ''} ${option.lastNameEn || ''}`.trim() || 'Unknown'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }} noWrap>
                {[option.uid, option.passportNumber, option.mobileNumber].filter(Boolean).join(' | ')}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Employee"
            placeholder="Search by name, UID, passport, or phone..."
            required={required}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: <MdPersonSearch size={20} style={{ marginRight: 8, color: '#00838f' }} />,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default QuickEmployeeSearch;
